"use client";

/**
 * Fixed-bottom tab bar for mobile (< sm breakpoint). Replaces the
 * hamburger menu in SharedHeader so the top bar stays uncluttered
 * and the primary destinations are always thumb-reachable.
 *
 * Lifecycle:
 * - Shown on mobile only; hidden from sm: up.
 * - Renders over both browser and PWA sessions - the installed-app
 *   vs browser distinction doesn't matter for a mobile user who
 *   lands here from Google and stays on the site.
 * - Layout pushes content with pb-20 via a global rule in the
 *   [locale]/layout.tsx, so pages don't have to opt in individually.
 * - Tap "More" opens MobileMoreSheet for destinations that didn't
 *   earn a slot (language, install, profile, legal links).
 *
 * Five slots max - any more and icons start cramping on 360 px
 * phones. Auth-aware: slot 4 flips between History (signed in)
 * and Sign in (anon) so the bar always has a useful action there.
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { usePathname } from "next/navigation";
import { useLocalizedRouter } from "@/lib/localized-router";
import { Link2, MapPin, Calculator, MoreHorizontal, Search, MessageCircle, Crown, Bell, BarChart3 } from "lucide-react";
import { useAdaptiveSlot } from "@/lib/adaptive-nav";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { useLatestRelease } from "@/lib/use-releases";
import { hasProFeatures } from "@/lib/pro";
import { haptic } from "@/lib/haptics";
import MobileMoreSheet from "./MobileMoreSheet";
import type { TranslationKey } from "@/lib/i18n";

interface Tab {
  key: string;
  href?: string;
  labelKey: TranslationKey;
  icon: typeof Link2;
  /** Exact-match paths that should light the tab up as active. The
   *  first entry is the `href` for navigation; later entries are
   *  additional paths that should share this tab's active styling
   *  (e.g. listing detail under /h/:id lights up Analyze). */
  matchPaths?: string[];
}

const ANALYZE: Tab = {
  key: "analyze",
  href: "/analyze",
  labelKey: "nav.analyze",
  // Chain-link glyph - the analyze flow's hero action is "paste a
  // bina.az URL", so a link icon reads more truthfully than the
  // generic Home glyph it replaced.
  icon: Link2,
  // Landing page ("/") is deliberately NOT in matchPaths - the
  // marketing hero is a distinct destination from the analyze tool;
  // lighting the Analyze tab there misleads users into thinking
  // they're already inside the tool. All other routes that render
  // analysis results still share this tab's active state.
  matchPaths: ["/analyze", "/s", "/h"],
};
// MapPin, not the folded-map glyph: user testing read the fold as "a
// document"; the pin is the primitive everyone knows means "map".
const MAP: Tab = { key: "map", href: "/map", labelKey: "nav.map", icon: MapPin };
const SELL: Tab = { key: "sell", href: "/calculate", labelKey: "nav.sell", icon: Calculator };
// Listings replaced Compare in the mobile bar on 2026-06-04. Listings
// is a high-traffic browse surface that benefits from thumb-reachable
// access; Compare moved into the MobileMoreSheet - still one tap away.
const LISTINGS: Tab = {
  key: "listings",
  href: "/listings",
  labelKey: "nav.listings",
  icon: Search,
  matchPaths: ["/listings"],
};
// Chat replaces the previous Profile / Sign-in slot on 2026-04-26.
// Profile now lives in the top SharedHeader (AuthButton, visible on
// every breakpoint); the bottom bar uses that slot for Chat which is
// a primary action and was previously buried in More. Anon users land
// on /auth/login when they tap Chat - the chat page itself enforces
// auth via its own redirect, no special-casing needed in the tab bar.
const CHAT: Tab = { key: "chat", href: "/chat", labelKey: "nav.chat", icon: MessageCircle };
// Pro takes Chat's slot for users WITHOUT Pro features (2026-07-12):
// pricing is the conversion page and was one tap deep in the More
// sheet, i.e. invisible. For anon users the Chat tab was a login
// redirect anyway; free users keep Chat one tap away in the More
// sheet. Subscribers AND active-trial users see Chat here - both
// already have Pro features, so a pricing tab is dead weight (the
// header chip carries the trial countdown).
const PRO: Tab = { key: "pro", href: "/pricing", labelKey: "nav.pro", icon: Crown };
// The fourth slot follows the intent (lib/adaptive-nav): deal alerts for
// an investor, listing analytics for an agent, the map otherwise.
const ALERTS: Tab = { key: "alerts", href: "/alerts", labelKey: "nav.alerts", icon: Bell, matchPaths: ["/alerts", "/watchlist"] };
const ANALYTICS: Tab = { key: "analytics", href: "/listing-analytics", labelKey: "nav.analytics", icon: BarChart3 };

export default function MobileTabBar() {
  const t = useT();
  const { user } = useAuth();
  // A dot on More while the newest release is unseen in this browser
  // (lib/use-releases.ts): the sheet's "What's new" row carries the
  // same dot, and opening the release clears both. Hidden while the
  // sheet is open, where the row itself says it.
  // The release dot on More: for everyone the release is news to
  // (lib/use-releases), the same rule as the header entry.
  const { dot: releaseUnseen } = useLatestRelease(user);
  const pathname = usePathname();
  const router = useLocalizedRouter();
  const rawPath = pathname.replace(/^\/(az|ru|en)/, "") || "/";
  const [moreOpen, setMoreOpen] = useState(false);

  // The chat page used to hide this bar entirely (its own composer
  // sat at the viewport bottom, the bar would overlap). Users
  // reported feeling stranded - they tapped Chat, lost the rest of
  // the app's navigation, and bounced. ChatPage now reserves
  // bottom-[calc(4rem+env(safe-area-inset-bottom))] for the bar on
  // mobile so the composer lives above it; we just render normally
  // here.
  // Five tabs + More. The fifth slot is tier-aware: Pro (pricing) for
  // anon/free users, Chat for subscribers and active trials - see the
  // PRO const's note.
  const isPaid = hasProFeatures(user);
  const slot = useAdaptiveSlot();
  const fourth = slot === "alerts" ? ALERTS : slot === "analytics" ? ANALYTICS : MAP;
  // An investor keeps the deal alerts and gets the map in the fifth
  // slot; chat and pricing wait in the More sheet (owner, 2026-10-10:
  // "I'm an investor, why don't I see the map? instead of chat").
  const fifth = slot === "alerts" ? MAP : isPaid ? CHAT : PRO;
  const tabs: Tab[] = [ANALYZE, SELL, LISTINGS, fourth, fifth];

  const matches = (tab: Tab): boolean => {
    if (!tab.href) return false;
    // Prefix match on the tab's own href so sub-pages light their
    // parent tab: /calculate/mortgage -> Calculate, /map/* -> Map.
    // The trailing slash keeps siblings apart (/land-map is not /map).
    if (rawPath === tab.href || rawPath.startsWith(`${tab.href}/`)) return true;
    if (tab.matchPaths?.some((p) => rawPath === p || rawPath.startsWith(`${p}/`))) return true;
    return false;
  };

  // Routes whose nav home is the More sheet (no primary slot of their
  // own). When the user is on one of them, the More button carries the
  // active tint so the bar still says "you are somewhere in here".
  const MORE_ROUTES = [
    "/compare", "/analyst", "/history", "/alerts", "/watchlist", "/properties",
    "/invest", "/residences", "/residence",
    // Chat is homed in the More sheet for non-paid users (its bar slot
    // shows Pro for them).
    ...(isPaid && slot !== "alerts" ? [] : ["/chat"]),
    ...(slot === "alerts" ? ["/pricing"] : []),
  ];
  const moreActive = MORE_ROUTES.some(
    (p) => rawPath === p || rawPath.startsWith(`${p}/`),
  );

  // Directional slide between tab pages via the View Transitions API
  // (progressive enhancement: no-op on browsers without it or with
  // reduced motion). Direction = target tab index vs the current one,
  // stamped on <html> so globals.css picks the matching keyframes.
  // The pending-navigation promise resolves on the pathname change so
  // the API captures the NEW page, with a timeout backstop.
  const vtResolve = useRef<(() => void) | null>(null);
  // Optimistic active tab: lights up ON TAP, before the route commits.
  // Route rendering takes a few hundred ms on the heavy tabs, and with
  // nothing acknowledging the tap that read as lag. Cleared once the
  // pathname actually changes (or if navigation never lands).
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  useEffect(() => {
    vtResolve.current?.();
    vtResolve.current = null;
    setPendingKey(null);
  }, [pathname]);
  function slideTo(e: React.MouseEvent, tab: Tab, href: string) {
    const doc = document as Document & {
      startViewTransition?: (cb: () => Promise<void>) => { finished: Promise<void> };
    };
    if (
      !doc.startViewTransition
      || window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return false;
    const fromIdx = tabs.findIndex((x) => matches(x));
    const toIdx = tabs.findIndex((x) => x.key === tab.key);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return false;
    e.preventDefault();
    document.documentElement.dataset.navDir = toIdx > fromIdx ? "fwd" : "back";
    const vt = doc.startViewTransition(() => {
      router.push(href);
      return new Promise<void>((resolve) => {
        vtResolve.current = resolve;
        // Backstop: if the route hasn't committed by now, play the
        // transition with whatever is on screen rather than holding
        // the UI frozen. 350ms covers prefetched tabs; slow commits
        // then land without a slide instead of blocking the tap.
        window.setTimeout(resolve, 350);
      });
    });
    vt.finished.finally(() => {
      delete document.documentElement.dataset.navDir;
    });
    return true;
  }

  return (
    <>
      {/* The bar itself. `pb-[env(safe-area-inset-bottom)]` keeps the
          icons above the iOS home-indicator on the notch generation.
          `sm:hidden` confines it to the mobile breakpoint; desktop keeps
          the SharedHeader top nav. */}
      <nav
        aria-label={t("a11y.primary_nav")}
        data-fullscreen-hide
        // Own view-transition-name: excluded from the sliding root
        // snapshot during tab navigation, so the bar stays rock still
        // while the page content slides (globals.css pins its group
        // to animation: none).
        style={{ viewTransitionName: "tabbar" } as React.CSSProperties}
        className="fixed bottom-0 inset-x-0 z-[1100] sm:hidden border-t border-gray-200 bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      >
        {/* `min-w-0` + `truncate` on the label is what keeps the bar
            from blowing out on 300-320px viewports (Galaxy Fold
            cover screen, iPhone SE 1st gen, etc.). Each flex-1
            column gets ~50px at 300vw; the longest AZ label
            ("Elan təhlili") is wider than that at text-xs, so
            without min-w-0 the inner span would push the column
            wider than its share and the row would horizontally
            scroll. Smaller base text + tighter horizontal padding
            also buy a few more characters before truncation kicks
            in on tiny phones. */}
        {/* Fixed h-16: the columns' intrinsic height came to ~63px,
            leaving a 1px daylight seam under every sticky CTA offset
            by the shared 4rem constant. A deterministic 64px makes
            the bars stack flush. */}
        <div className="flex h-16 items-stretch">
          {tabs.map((tab) => {
            // Optimistic: the TAPPED tab is active from the moment of
            // the tap; the previous one stands down at the same time.
            const active = pendingKey ? pendingKey === tab.key : matches(tab);
            const Icon = tab.icon;
            return (
              <Link
                key={tab.key}
                href={tab.href!}
                // Haptic on pointerdown, not click: click waits for
                // touchend + whatever main-thread work navigation
                // kicks off, which read as a half-second-late buzz.
                // Finger contact IS the moment the tick belongs to.
                onPointerDown={() => haptic(6)}
                onClick={(e) => {
                  if (!matches(tab)) setPendingKey(tab.key);
                  // Direct /map -> /listings hop carries the live map
                  // filters into the list (mirrors SharedHeader's
                  // desktop nav). Read at click time so the header
                  // needs no Suspense boundary.
                  const rawPath = pathname.replace(/^\/(az|ru|en)/, "") || "/";
                  if (tab.href === "/listings" && rawPath === "/map") {
                    const params = new URLSearchParams(window.location.search);
                    params.delete("layer");
                    params.delete("page");
                    const qs = params.toString();
                    if (qs) {
                      e.preventDefault();
                      router.push(`/listings?${qs}`);
                      return;
                    }
                  }
                  slideTo(e, tab, tab.href!);
                }}
                className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 px-0.5 py-2.5 text-xs font-medium transition-colors ${
                  // Every tab the same gray, only the active one dark (the
                  // quiet UI, 2026-10-29; it was indigo): the Pro tab's
                  // permanent violet read as a second selected tab
                  // (owner, 2026-10-18).
                  active
                    ? "font-semibold text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-6 h-6 shrink-0" strokeWidth={active ? 2.25 : 1.75} />
                <span className="w-full truncate text-center">{t(tab.labelKey)}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onPointerDown={() => haptic(6)}
            onClick={() => setMoreOpen(true)}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-0.5 py-2 text-xs font-medium transition-colors ${
              moreOpen || moreActive
                ? "font-semibold text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
            aria-expanded={moreOpen}
          >
            <span className="relative shrink-0">
              <MoreHorizontal
                className="w-6 h-6"
                strokeWidth={moreOpen || moreActive ? 2.25 : 1.75}
              />
              {releaseUnseen && !moreOpen && (
                <>
                  <span aria-hidden className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-gray-400 ring-2 ring-white" />
                  <span className="sr-only">{t("wn.unseen")}</span>
                </>
              )}
            </span>
            <span className="w-full truncate text-center">{t("nav.more")}</span>
          </button>
        </div>
      </nav>

      <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
